/**
 * Camera.ts — orbital camera with REVERSE-Z perspective projection.
 *
 * Conventions:
 *  - Y-up, right-handed.
 *  - near = 0.05 m, far = 3000 m (project default).
 *  - Reverse-Z: projection maps near→z=1, far→z=0; pipelines use 'greater-equal'.
 *  - Mouse: left drag = orbit, scroll = zoom (distance), right drag = pan target.
 *  - Camera.position is exposed for shaders (e.g. Fresnel V vector, fog distance).
 *
 * The shared CameraUniforms buffer matches `struct Camera` in shaders/common.wgsl.
 */

import { mat4, vec3 } from "wgpu-matrix";
import { createUniformBuffer } from "./gpu/Buffers";

const STRUCT_SIZE = 64 * 4 + 16 + 16; // 4 mat4x4 + position+near + forward+far

export interface CameraOptions {
  fovYRadians?: number;
  near?: number;
  far?: number;
  initialDistance?: number;
  initialAzimuth?: number;
  initialElevation?: number;
  target?: [number, number, number];
}

export class Camera {
  readonly buffer: GPUBuffer;
  private cpu = new ArrayBuffer(STRUCT_SIZE);
  private f32 = new Float32Array(this.cpu);

  fovY: number;
  near: number;
  far: number;
  aspect = 1;

  // Orbital state.
  target: Float32Array;
  distance: number;
  /** Azimuth around Y axis (radians). 0 = looking from +Z toward origin. */
  azimuth: number;
  /** Elevation above horizontal (radians). Clamped within (-π/2 + ε, π/2 - ε). */
  elevation: number;

  position = vec3.create(0, 0, 0);
  forward = vec3.create(0, 0, -1);

  view = mat4.identity();
  proj = mat4.identity();
  viewProj = mat4.identity();
  invViewProj = mat4.identity();

  constructor(
    private device: GPUDevice,
    opts: CameraOptions = {},
  ) {
    this.fovY = opts.fovYRadians ?? (60 * Math.PI) / 180;
    this.near = opts.near ?? 0.05;
    this.far = opts.far ?? 3000;
    this.distance = opts.initialDistance ?? 600;
    this.azimuth = opts.initialAzimuth ?? Math.PI * 0.25;
    this.elevation = opts.initialElevation ?? (25 * Math.PI) / 180;
    const t = opts.target ?? [0, 0, 0];
    this.target = new Float32Array([t[0], t[1], t[2]]);
    this.buffer = createUniformBuffer(device, STRUCT_SIZE, "CameraUniforms");
    this.recompute();
  }

  setAspect(aspect: number): void {
    if (aspect === this.aspect) return;
    this.aspect = aspect;
    this.recompute();
  }

  /** Recompute view/proj from orbital state and upload. */
  recompute(): void {
    // Orbital position in world space.
    const ce = Math.cos(this.elevation);
    const se = Math.sin(this.elevation);
    const sa = Math.sin(this.azimuth);
    const ca = Math.cos(this.azimuth);
    const offset = [
      this.distance * ce * sa,
      this.distance * se,
      this.distance * ce * ca,
    ];
    this.position[0] = this.target[0] + offset[0];
    this.position[1] = this.target[1] + offset[1];
    this.position[2] = this.target[2] + offset[2];

    mat4.lookAt(this.position, this.target, [0, 1, 0], this.view);
    // Reverse-Z perspective: swap near/far in the standard formula.
    perspectiveReverseZ(this.fovY, this.aspect, this.near, this.far, this.proj);
    mat4.multiply(this.proj, this.view, this.viewProj);
    mat4.invert(this.viewProj, this.invViewProj);

    // forward = -view.row2 in world (camera looks toward -Z in view space).
    this.forward[0] = this.target[0] - this.position[0];
    this.forward[1] = this.target[1] - this.position[1];
    this.forward[2] = this.target[2] - this.position[2];
    const fLen =
      Math.hypot(this.forward[0], this.forward[1], this.forward[2]) || 1;
    this.forward[0] /= fLen;
    this.forward[1] /= fLen;
    this.forward[2] /= fLen;

    this.uploadBuffer();
  }

  private uploadBuffer(): void {
    let i = 0;
    for (let m = 0; m < 16; m++) this.f32[i++] = this.viewProj[m]!;
    for (let m = 0; m < 16; m++) this.f32[i++] = this.view[m]!;
    for (let m = 0; m < 16; m++) this.f32[i++] = this.proj[m]!;
    for (let m = 0; m < 16; m++) this.f32[i++] = this.invViewProj[m]!;
    this.f32[i++] = this.position[0];
    this.f32[i++] = this.position[1];
    this.f32[i++] = this.position[2];
    this.f32[i++] = this.near;
    this.f32[i++] = this.forward[0];
    this.f32[i++] = this.forward[1];
    this.f32[i++] = this.forward[2];
    this.f32[i++] = this.far;
    this.device.queue.writeBuffer(this.buffer, 0, this.cpu);
  }

  destroy(): void {
    this.buffer.destroy();
  }
}

/**
 * Reverse-Z perspective projection (right-handed, depth in [0, 1]).
 * Maps near plane to z=1, far plane to z=0.
 *
 * Derivation: standard RH proj with `depth = (zNear)/(zNear - zFar) + (zFar*zNear)/((zFar-zNear)*z_view)`
 * for a depth in [0,1]. Reverse-Z swaps near and far: substitute (near<->far).
 */
function perspectiveReverseZ(
  fovY: number,
  aspect: number,
  near: number,
  far: number,
  out: Float32Array,
): Float32Array {
  const f = 1 / Math.tan(fovY * 0.5);
  // Column-major (wgpu-matrix uses column-major like WebGPU).
  out[0] = f / aspect;
  out[1] = 0;
  out[2] = 0;
  out[3] = 0;

  out[4] = 0;
  out[5] = f;
  out[6] = 0;
  out[7] = 0;

  out[8] = 0;
  out[9] = 0;
  // Reverse-Z: A = -near / (far - near), B = far*near / (far - near).
  // For RH camera looking toward -Z (z_view < 0), depth = A + B / (-z_view).
  // At z_view = -near → depth = -near/(far-near) - far*near/((far-near)*near) = -(near+far)/(far-near)?
  // Use standard derivation: produce depth=1 at near, 0 at far for RH.
  out[10] = near / (far - near);
  out[11] = -1;

  out[12] = 0;
  out[13] = 0;
  out[14] = (far * near) / (far - near);
  out[15] = 0;
  return out;
}
